import React, { useCallback, useState, useRef } from 'react';
import { TextField, useTheme, alpha, InputAdornment } from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

// Helper function to format number with commas
const formatNumberWithCommas = (value: string): string => {
  // Remove any existing commas and parse the number
  const num = parseFloat(value.replace(/,/g, ''));
  if (isNaN(num)) return value;

  // Format with commas and 2 decimal places
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Helper function to remove commas and format to 2 decimals
const unformatNumber = (value: string): string => {
  // Remove commas and parse
  const num = parseFloat(value.replace(/,/g, ''));
  if (isNaN(num)) return value;

  // Return with 2 decimal places
  return num.toFixed(2);
};

type Field = 'sellingprice';

interface SellingPriceInputProps {
  sellingPrice: string;
  setSellingPrice: React.Dispatch<React.SetStateAction<string>>;
  lastSellingPrice: string;
  setLastSellingPrice: React.Dispatch<React.SetStateAction<string>>;
  handleBlur: (field: Field, value: string) => void;
  handleChange: (
    field: Field,
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  width?: string | number;
  disabled?: boolean;
}

export const SellingPriceInput: React.FC<SellingPriceInputProps> = ({
  sellingPrice,
  setSellingPrice,
  lastSellingPrice,
  setLastSellingPrice,
  handleBlur,
  handleChange,
  width = '100%',
  disabled = false,
}) => {
  const theme = useTheme();
  const [isFocused, setIsFocused] = useState(false);
  const originalValueRef = useRef<string>(sellingPrice);

  // Format the display value with commas only when not focused
  const displayValue = isFocused ? sellingPrice : formatNumberWithCommas(sellingPrice);

  // Handle focus to select all text and track original value
  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      originalValueRef.current = sellingPrice;
      e.target.select();
    },
    [sellingPrice]
  );

  // Custom change handler to handle raw number input
  const handleValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      // Allow only numbers and decimal point
      if (/^\d*\.?\d{0,2}$/.test(value) || value === '') {
        setSellingPrice(value);
      }
    },
    [setSellingPrice]
  );

  // Handle blur to commit changes
  const handleBlurInternal = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);

      // Unformat the value (remove commas) before processing
      const unformattedValue = unformatNumber(e.target.value);

      // Only commit if value has changed
      if (unformattedValue !== originalValueRef.current) {
        setSellingPrice(unformattedValue);
        setLastSellingPrice(unformattedValue);
        handleBlur('sellingprice', unformattedValue);
      }
    },
    [handleBlur, setSellingPrice, setLastSellingPrice]
  );

  // Handle key down events
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();

        // Commit changes if value has changed
        if (sellingPrice !== originalValueRef.current) {
          const unformattedValue = unformatNumber(sellingPrice);
          setSellingPrice(unformattedValue);
          setLastSellingPrice(unformattedValue);
          handleBlur('sellingprice', unformattedValue);
        }

        // Remove focus
        (e.target as HTMLInputElement).blur();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();

        // Revert to original value
        setSellingPrice(originalValueRef.current);

        // Remove focus
        (e.target as HTMLInputElement).blur();
      }
    },
    [sellingPrice, handleBlur, setSellingPrice, setLastSellingPrice]
  );

  return (
    <TextField
      disabled={disabled}
      type="text"
      value={displayValue}
      onChange={handleValueChange}
      onBlur={handleBlurInternal}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      variant="outlined"
      size="medium"
      sx={{
        width: width,
        '& .MuiOutlinedInput-root': {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.common.white, 0.15)
              : alpha(theme.palette.primary.main, 0.05),
          fontWeight: 700,
          borderRadius: 1,
          transition: 'all 0.2s ease',
          width: '100%',
          '&:hover': {
            backgroundColor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.2)
                : alpha(theme.palette.primary.main, 0.08),
          },
          '& fieldset': {
            borderColor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.5)
                : alpha(theme.palette.primary.main, 0.5),
            borderWidth: 2,
          },
          '&:hover fieldset': {
            borderColor:
              theme.palette.mode === 'dark'
                ? theme.palette.common.white
                : theme.palette.primary.main,
            borderWidth: 2,
          },
          '&.Mui-focused fieldset': {
            borderColor:
              theme.palette.mode === 'dark'
                ? theme.palette.common.white
                : theme.palette.primary.main,
            borderWidth: 2,
            boxShadow: `0 0 0 2px ${
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.2)
                : alpha(theme.palette.primary.main, 0.2)
            }`,
          },
        },
        '& .MuiInputBase-input': {
          color:
            theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.text.primary,
          fontSize: '1.15rem',
          padding: theme.spacing(1),
          fontWeight: 700,
          width: '100%',
          minWidth: '150px',
          '&::placeholder': {
            color:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.7)
                : alpha(theme.palette.text.primary, 0.7),
            opacity: 1,
          },
        },
        '& .MuiInputAdornment-root': {
          color:
            theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.text.primary,
          marginRight: theme.spacing(1),
        },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <AttachMoneyIcon
              sx={{
                color:
                  theme.palette.mode === 'dark'
                    ? theme.palette.common.white
                    : theme.palette.text.primary,
              }}
              aria-hidden="true"
            />
          </InputAdornment>
        ),
        inputProps: {
          min: 0,
          max: 99999999.99,
          step: '0.01',
          'aria-label': 'Enter total selling price',
          style: {
            textAlign: 'left',
            fontWeight: 700,
            fontSize: '1.15rem',
            color:
              theme.palette.mode === 'dark'
                ? theme.palette.common.white
                : theme.palette.text.primary,
          },
        },
      }}
    />
  );
};
